import { Controller } from "@hotwired/stimulus";
import { createConsumer } from "@rails/actioncable";

const consumer = createConsumer();

export default class extends Controller {
  static values = { dropId: String };
  static targets = ["count", "badge"];

  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return;

    console.log("🟢 Stimulus Connected for Drop ID:", this.dropIdValue);

    // THE TURBO CACHE FIX:
    // Listen for the exact moment Turbo is about to snapshot this page.
    // We forcefully hide the badge and clear the text so Turbo saves a clean slate.
    this.beforeCacheHandler = () => {
      if (this.hasBadgeTarget) {
        this.badgeTarget.classList.add("hidden");
        this.badgeTarget.classList.remove("flex");
      }
      if (this.hasCountTarget) {
        this.countTarget.textContent = "";
      }
    };
    document.addEventListener("turbo:before-cache", this.beforeCacheHandler);

    let visitorId = sessionStorage.getItem("sauda_visitor_id");
    if (!visitorId) {
      visitorId = crypto.randomUUID();
      sessionStorage.setItem("sauda_visitor_id", visitorId);
    }

    this.channel = consumer.subscriptions.create(
      {
        channel: "DropPresenceChannel",
        drop_id: this.dropIdValue,
        visit_id: visitorId,
      },
      {
        connected: () => {
          console.log(
            "⚡ ActionCable WebSocket Connection Established successfully!",
          );
          this.pingTimer = setInterval(() => {
            this.channel.perform("ping");
          }, 3000);
        },
        disconnected: () => {
          console.log("❌ ActionCable WebSocket Disconnected.");
          this.clearPingTimer();
        },
        received: (data) => {
          console.log("📥 Received WebSocket payload:", data);
          this.updateCount(data);
        },
      },
    );
  }

  disconnect() {
    console.log("🔴 Stimulus Controller Disconnected from DOM");

    // Clean up the global event listener to prevent memory leaks
    if (this.beforeCacheHandler) {
      document.removeEventListener(
        "turbo:before-cache",
        this.beforeCacheHandler,
      );
    }

    this.clearPingTimer();

    if (this.channel) {
      this.channel.unsubscribe();
      consumer.subscriptions.remove(this.channel);
      this.channel = null;
    }
  }

  clearPingTimer() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  updateCount(data) {
    if (!this.hasBadgeTarget || !this.hasCountTarget) return;

    if (data.count <= 1) {
      this.badgeTarget.classList.add("hidden");
      this.badgeTarget.classList.remove("flex");
      return;
    }

    const formattedNumber = this.formatViewerCount(data.count);
    const previousText = this.countTarget.textContent;

    this.badgeTarget.classList.remove("hidden");
    this.badgeTarget.classList.add("flex");
    this.countTarget.textContent = formattedNumber;

    if (previousText !== formattedNumber && previousText !== "") {
      this.badgeTarget.classList.add("scale-105", "border-[#E47434]/40");

      setTimeout(() => {
        if (this.hasBadgeTarget) {
          this.badgeTarget.classList.remove("scale-105", "border-[#E47434]/40");
        }
      }, 300);
    }
  }

  formatViewerCount(count) {
    if (count >= 10000000) {
      return (count / 10000000).toFixed(1).replace(/\.0$/, "") + "Cr";
    }
    if (count >= 100000) {
      return (count / 100000).toFixed(1).replace(/\.0$/, "") + "L";
    }
    if (count >= 1000) {
      return (count / 1000).toFixed(1).replace(/\.0$/, "") + "K";
    }
    return count.toString();
  }
}
